import "./list-service.scss";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useQueryClient } from "react-query";
import { QueryKeyService } from "@factory/model/query-model-service";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetService } from "@factory/query/service.query";
import { GetServiceModel } from "@factory/model/query-model-service";
import { TableSort } from "@shared/models/table-sort";
import { FilterService } from "../filter-service/filter-service";
import { useFilterServiceContext } from "../filter-service/filter-service.context";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { numberFormat } from "@shared/utility/formats/number-format";
import DotLabel from "@shared/ui/dot-label/dot-label";
import { useTransactionUtil } from "@core/utility/transaction.helper";

export function ListService() {
  // THIS IS THE LOCAL DECLARATION
  const { convertMinutesToHours } = useTransactionUtil();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterServiceContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: serviceResponse,
    isLoading,
    isError,
    error,
  } = useQyGetService(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  const queryClient = useQueryClient();
  const [servicesList, setServicesList] = useState<GetServiceModel[]>([]);

  useEffect(() => {
    if (serviceResponse?.data) {
      setServicesList(serviceResponse.data);
    }
  }, [serviceResponse?.data]);

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetServiceModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterService />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{serviceResponse?.count}</b>
      </h5>

      <DataTable
        value={servicesList}
        reorderableRows
        onRowReorder={(e) => {
          setServicesList(e.value);
          const codes = e.value.map((item: any) => item.code);
          localStorage.setItem("services_custom_order", JSON.stringify(codes));
          queryClient.invalidateQueries(QueryKeyService);
        }}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column rowReorder style={{ width: "3rem" }} />
        <Column field="name" header="Name" sortable></Column>
        <Column field="description" header="Description" sortable></Column>
        <Column
          body={(item) => currencyFormat(item.amount)}
          field="amount"
          header="Amount"
          sortable
        ></Column>
        <Column field="days" header="Days" sortable></Column>
        <Column
          field="duration"
          body={(item) => `${convertMinutesToHours(item.duration)}hr(s)`}
          header="Duration"
          sortable
        ></Column>
        <Column
          body={(item) => (
            <DotLabel
              label={Boolean(item.is_open_time) ? "Yes" : "No"}
              classBg={
                Boolean(item.is_open_time) ? "bg-green-500" : "bg-blue-500"
              }
            />
          )}
          field="is_open_time"
          header="Open Time"
        ></Column>
        <Column
          body={(item) => (
            <DotLabel
              label={Boolean(item.is_subscription) ? "Yes" : "No"}
              classBg={
                Boolean(item.is_subscription) ? "bg-green-500" : "bg-blue-500"
              }
            />
          )}
          field="is_subscription"
          header="Subscription"
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={serviceResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListService" className="service">
      <HeaderContent title="Service">
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListService;
